'use client';;!function(){try{var e="undefined"!=typeof window?window:"undefined"!=typeof global?global:"undefined"!=typeof globalThis?globalThis:"undefined"!=typeof self?self:{};e._sentryModuleMetadata=e._sentryModuleMetadata||{},e._sentryModuleMetadata[(new e.Error).stack]=Object.assign({},e._sentryModuleMetadata[(new e.Error).stack],{"_sentryBundlerPluginAppKey:native-teams-frontend":true});}catch(e){}}();

import SumsubWebSdk from '@sumsub/websdk-react';
import { useQueryClient } from '@tanstack/react-query';
import { Button } from '@workspace/ui/components/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@workspace/ui/components/dialog';
import { ShieldCheck } from 'lucide-react';
import { useTheme } from 'next-themes';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useCountries } from '@/hooks/use-countries';
import {
  hasEpsCardPermission,
  useCurrentKYCVerification,
  useInitiateKYCVerification,
  useKYCVerifications,
} from '@/hooks/use-kyc';
import type { ReviewCardFormData } from '@/lib/kyc-data-mapper';
import { mapKYCDataToFormData } from '@/lib/kyc-data-mapper';
import type { CurrentKYCVerification } from '@/types/kyc';

const PENDING_REVIEW_STATUSES = ['pending', 'awaitingService', 'onHold', 'awaitingUser'];
const KYC_POLL_ATTEMPTS = 15;
const KYC_POLL_DELAY_MS = 2000;

function isSumsubReviewPending(sumsubStatus?: string, reviewStatus?: string): boolean {
  if (sumsubStatus === 'pending') return true;
  if (!reviewStatus) return false;
  return PENDING_REVIEW_STATUSES.includes(reviewStatus);
}

function shouldContinueKycPoll(
  current: CurrentKYCVerification | null | undefined,
  reviewPending: boolean,
  waitForReadyToOrder: boolean
): boolean {
  const currentStatus = current?.status;
  if (currentStatus === 'final_rejected') return false;
  if (currentStatus === 'approved' && hasEpsCardPermission(current)) return false;
  // After a resubmit the current KYC can still show the previous rejection.
  if (currentStatus === 'rejected') return reviewPending || waitForReadyToOrder;

  const currentStillSettling =
    !currentStatus || currentStatus === 'pending' || currentStatus === 'initial';
  const waitingForCardPermission =
    waitForReadyToOrder && (currentStillSettling || currentStatus === 'approved');

  return reviewPending || waitingForCardPermission;
}

// Config will be created dynamically in the component based on theme

interface KYCVerificationDialogProps {
  onKYCSuccess?: (prefillData: Partial<ReviewCardFormData>) => void;
  isKYCDialogOpen: boolean;
  setIsKYCDialogOpen: (open: boolean) => void;
  title?: string;
  description?: string;
  onClose?: () => void;
  /** Card ordering opens Sumsub directly. Other callers keep the simple prompt first. */
  variant?: 'simple' | 'direct';
}

export function KYCVerificationDialog({
  onKYCSuccess,
  isKYCDialogOpen,
  setIsKYCDialogOpen,
  title = 'Verify your identity to order your card',
  description = 'Before proceeding with your card order, please complete our quick and easy KYC verification process. This ensures security and compliance. Once your KYC is successfully passed, you can proceed to order your card.',
  onClose,
  variant = 'simple',
}: KYCVerificationDialogProps) {
  const [isSumsubDialogOpen, setIsSumsubDialogOpen] = useState(false);
  const [_isLoadingKYCData, setIsLoadingKYCData] = useState(false); // eslint-disable-line @typescript-eslint/no-unused-vars
  const [hasTriggeredReview, setHasTriggeredReview] = useState(false);
  const [pendingKYCSuccessData, setPendingKYCSuccessData] =
    useState<Partial<ReviewCardFormData> | null>(null);
  const [kycData, setKycData] = useState<{
    sumsub_token: string;
    iframe_url: string;
    status: string;
  } | null>(null);
  const [directStartError, setDirectStartError] = useState<string | null>(null);
  const directStartBlocked = useRef(false);
  const kycMutation = useInitiateKYCVerification();
  const { data: countries } = useCountries();
  const { data: kycVerifications, refetch: refetchKYCVerifications } = useKYCVerifications();
  const { data: currentKyc, refetch: refetchCurrentKyc } = useCurrentKYCVerification();
  const queryClient = useQueryClient();
  const { resolvedTheme } = useTheme();

  // Create dynamic config based on current theme
  const config = {
    theme: resolvedTheme === 'dark' ? 'dark' : 'light',
    customizationName: 'Native Teams: Wallet 2025', // overrides level customization
    // translationName: 'MySDK2Translations', // optional
  };

  // Check for existing EPS-eligible KYC when dialog opens.
  // Only skip straight to the review dialog when the user already has the EPS card
  // permission — a demo/QA/admin "approved" verification is not enough.
  // This branch is card-order specific; skip it when onKYCSuccess is not provided.
  useEffect(() => {
    if (!onKYCSuccess) return;
    if (
      isKYCDialogOpen &&
      hasEpsCardPermission(currentKyc) &&
      kycVerifications?.[0] &&
      countries?.length &&
      !isSumsubDialogOpen
    ) {
      console.log('✅ Found existing EPS-eligible KYC, skipping verification dialog');
      // User already has EPS card permission, skip to review dialog
      const kycData = kycVerifications[0];
      if (!kycData) {
        console.warn('KYC data is null or undefined, cannot map to form data');
        return;
      }
      const mappedData = mapKYCDataToFormData(kycData, countries);
      setIsKYCDialogOpen(false); // Close KYC dialog
      onKYCSuccess(mappedData); // Notify parent to open review dialog
    }
  }, [
    isKYCDialogOpen,
    currentKyc,
    kycVerifications,
    countries,
    isSumsubDialogOpen,
    onKYCSuccess,
    setIsKYCDialogOpen,
  ]);

  const { mutate: initiateKyc } = kycMutation;

  const handleStartVerification = useCallback(() => {
    initiateKyc('basic-kyc-level', {
      onSuccess: (data) => {
        console.log('KYC verification success:', data);
        setKycData(data);
        setIsKYCDialogOpen(false);
        setIsSumsubDialogOpen(true);
      },
      onError: (error) => {
        console.log('KYC verification error:', error);
        if (variant !== 'direct') return;
        directStartBlocked.current = true;
        setDirectStartError("We couldn't start verification. Please try again.");
      },
    });
  }, [initiateKyc, setIsKYCDialogOpen, variant]);

  // The direct variant has no intro pop-up, so opening it starts Sumsub straight away.
  // EPS-eligible users are sent to the review dialog by the effect above instead.
  const shouldStartDirectly =
    variant === 'direct' &&
    isKYCDialogOpen &&
    !isSumsubDialogOpen &&
    !kycMutation.isPending &&
    !directStartError &&
    !directStartBlocked.current &&
    !hasEpsCardPermission(currentKyc);

  const closeDirectStartError = () => {
    setDirectStartError(null);
    setIsKYCDialogOpen(false);
    onClose?.();
  };

  useEffect(() => {
    if (!isKYCDialogOpen) directStartBlocked.current = false;
  }, [isKYCDialogOpen]);

  useEffect(() => {
    if (shouldStartDirectly) handleStartVerification();
  }, [shouldStartDirectly, handleStartVerification]);

  // The backend resolves the Sumsub webhook asynchronously, so an immediate refetch right
  // after a rejection event can still return a "pending" status. Poll a few times (stopping
  // early once the status settles) so the dashboard reflects the final status without a manual refresh.
  const refetchUntilStatusSettled = useCallback(
    async (waitForReadyToOrder = false) => {
      for (let attempt = 0; attempt < KYC_POLL_ATTEMPTS; attempt++) {
        const [{ data }, { data: current }] = await Promise.all([
          refetchKYCVerifications(),
          refetchCurrentKyc(),
        ]);
        const latest = data?.[0];
        const reviewPending = isSumsubReviewPending(
          latest?.sumsub?.status,
          latest?.sumsub?.review_details?.review_status
        );

        if (!shouldContinueKycPoll(current, reviewPending, waitForReadyToOrder)) return;

        await new Promise((resolve) => setTimeout(resolve, KYC_POLL_DELAY_MS));
      }
    },
    [refetchCurrentKyc, refetchKYCVerifications]
  );

  const handleClose = () => {
    // Only the card flow waits for the card permission after a completed review.
    const waitForReadyToOrder = hasTriggeredReview && !!onKYCSuccess;
    setIsKYCDialogOpen(false);
    setIsSumsubDialogOpen(false);
    setKycData(null);
    setIsLoadingKYCData(false);
    setHasTriggeredReview(false);
    // Make sure the dashboard container reflects the final KYC status (e.g. rejected)
    // even if the backend finished processing after the in-flow refetch.
    void refetchUntilStatusSettled(waitForReadyToOrder);
    // If verification completed while the user was reading the Sumsub success screen,
    // open the card order modal only after they explicitly close the dialog.
    if (pendingKYCSuccessData && onKYCSuccess) {
      const data = pendingKYCSuccessData;
      setPendingKYCSuccessData(null);
      onKYCSuccess(data);
    }
    onClose?.();
  };

  const handleSumsubMessage = useCallback(
    async (type: string, payload: { reviewStatus?: string } | unknown) => {
      console.log('Sumsub message type:', type, 'payload:', payload);

      // Debug: Log important events only
      if (
        type === 'idCheck.onApplicantVerificationCompleted' ||
        (type === 'idCheck.onApplicantStatusChanged' &&
          payload &&
          typeof payload === 'object' &&
          'reviewStatus' in payload)
      ) {
        console.log('🔍 KYC Event:', type, 'hasTriggeredReview:', hasTriggeredReview);
        if (
          type === 'idCheck.onApplicantStatusChanged' &&
          payload &&
          typeof payload === 'object' &&
          'reviewStatus' in payload
        ) {
          console.log('  - reviewStatus:', (payload as { reviewStatus: string }).reviewStatus);
        }
      }

      // Handle successful verification completion - prevent duplicate triggers
      if (
        !hasTriggeredReview &&
        (type === 'idCheck.onApplicantVerificationCompleted' ||
          (type === 'idCheck.onApplicantStatusChanged' &&
            payload &&
            typeof payload === 'object' &&
            'reviewStatus' in payload &&
            payload.reviewStatus === 'completed' &&
            'reviewResult' in payload &&
            payload.reviewResult &&
            typeof payload.reviewResult === 'object' &&
            'reviewAnswer' in payload.reviewResult &&
            payload.reviewResult.reviewAnswer === 'GREEN'))
      ) {
        // Immediately set the flag to prevent duplicate triggers
        setHasTriggeredReview(true);

        console.log('🎯 TRIGGERED: KYC verification completed successfully, fetching KYC data...');
        setIsLoadingKYCData(true);

        try {
          // Refetch KYC verifications (prefill source) and the current verification (permissions).
          // Invalidating the prefix also covers ['kyc-verification', 'demo-overage', currencyId].
          queryClient.invalidateQueries({ queryKey: ['kyc-verification'] });
          const [{ data: latestKYCVerifications }, { data: latestCurrentKyc }] = await Promise.all([
            refetchKYCVerifications(),
            refetchCurrentKyc(),
          ]);

          if (
            onKYCSuccess &&
            hasEpsCardPermission(latestCurrentKyc) &&
            latestKYCVerifications?.[0] &&
            countries?.length
          ) {
            console.log('EPS-eligible KYC data found:', latestKYCVerifications[0]);
            const kycData = latestKYCVerifications[0];
            if (!kycData) {
              console.warn('Latest KYC data is null or undefined, cannot map to form data');
              return;
            }
            const mappedData = mapKYCDataToFormData(kycData, countries);
            console.log('Mapped form data:', mappedData);
            // Store the data so the card order modal opens only after the user
            // explicitly closes the Sumsub success screen ("You may close this page").
            setPendingKYCSuccessData(mappedData);
          } else if (onKYCSuccess) {
            // The card permission can land after the Sumsub webhook. Keep polling the current
            // KYC so the wallet widget can move to ready-to-order. Do not open the order dialog.
            console.warn('EPS card permission not granted, not opening review dialog');
            void refetchUntilStatusSettled(true);
          }
        } catch (error) {
          console.error('Failed to fetch KYC data:', error);
          setIsSumsubDialogOpen(false);
        } finally {
          console.log('✅ KYC completed - Notifying parent');
          setIsLoadingKYCData(false);
        }
      }

      // Handle rejection — refetch KYC status but keep dialog open so user can read the rejection screen and close it themselves
      if (
        type === 'idCheck.onApplicantStatusChanged' &&
        payload &&
        typeof payload === 'object' &&
        'reviewStatus' in payload &&
        payload.reviewStatus === 'rejected'
      ) {
        console.log('KYC verification was rejected');
        void refetchUntilStatusSettled();
      }
    },
    [
      hasTriggeredReview,
      onKYCSuccess,
      refetchKYCVerifications,
      refetchCurrentKyc,
      refetchUntilStatusSettled,
      queryClient,
      countries,
    ]
  );

  return (
    <>
      {/* Main KYC Verification Dialog - matches the design in the image */}
      <Dialog
        open={isKYCDialogOpen && variant !== 'direct'}
        onOpenChange={(nextOpen) => {
          setIsKYCDialogOpen(nextOpen);
          if (!nextOpen && !isSumsubDialogOpen) onClose?.();
        }}
      >
        <DialogContent size="sm">
          <DialogHeader className="sr-only">
            <DialogTitle>{title}</DialogTitle>
          </DialogHeader>
          <div className="flex flex-col space-y-6">
            <div className="flex h-16 w-16 rounded-full">
              <ShieldCheck
                className="text-neutral-200 dark:text-neutral-100 h-14 w-14"
                strokeWidth={2.5}
              />
            </div>
            <div className="space-y-2 text-left">
              <p className="text-neutral-200 dark:text-neutral-100 text-[32px] leading-10 font-bold">
                {title}
              </p>
            </div>
            <p className="text-grey-800 dark:text-grey-400 text-left text-sm leading-relaxed">
              {description}
            </p>
            <Button
              variant="primary"
              className="w-full"
              onClick={handleStartVerification}
              isLoading={kycMutation.isPending}
              loadingText="Starting your verification"
            >
              Start your verification
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog
        open={variant === 'direct' && !!directStartError}
        onOpenChange={(open) => {
          if (!open) closeDirectStartError();
        }}
      >
        <DialogContent size="sm">
          <DialogHeader className="sr-only">
            <DialogTitle>Verification could not be started</DialogTitle>
          </DialogHeader>
          <div className="flex flex-col gap-4">
            <p className="text-neutral-200 dark:text-neutral-100 text-xl leading-6 font-medium">
              We couldn&apos;t start verification
            </p>
            <p className="text-grey-800 dark:text-grey-400 text-sm leading-5">{directStartError}</p>
            <Button type="button" className="w-full" onClick={closeDirectStartError}>
              Close
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Sumsub KYC Dialog - using regular Dialog like transaction-details-dialog */}
      <Dialog open={isSumsubDialogOpen} onOpenChange={(open) => !open && handleClose()}>
        <DialogContent className="max-h-[90vh] max-w-4xl p-0 sm:w-[574px] [&>.dialog-scrollbar]:overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="sr-only">KYC Verification</DialogTitle>
          </DialogHeader>

          {kycData && (
            <SumsubWebSdk
              className="h-full w-full md:pt-8"
              accessToken={kycData?.sumsub_token}
              expirationHandler={() => {
                console.log('Sumsub expiration handler');
              }}
              config={config}
              options={{
                addViewportTag: true,
                adaptIframeHeight: true,
                enableScrollIntoView: true,
              }}
              onMessage={handleSumsubMessage}
              onError={(error: Error) => {
                console.log('Sumsub error', error);
              }}
            />
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
